import { PageCanvas } from "@/components/page-canvas";
import { Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import SettingsLayout from "@/layouts/settings_layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  editLocationBayPath,
  locationSettingsLocationPath,
  newLocationBayPath,
} from "@/routes";
import type { Bay } from "./types";

export default function Bays({
  bays,
  proposal_hold_hours: holdHours,
}: {
  bays: Bay[];
  proposal_hold_hours: number;
}) {
  const { auth } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const locationId = auth.location.id;
  return (
    <SettingsLayout>
      <Head title={t("bays.title")} />
      <PageCanvas padded={false}>
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-page font-display">{t("bays.title")}</h1>
            <p className="text-muted-foreground mt-2 text-sm">
              {t("bays.description")}
            </p>
          </div>
          <Button render={<Link href={newLocationBayPath(locationId)} />}>
            {t("bays.add")}
          </Button>
        </header>
        <p className="text-muted-foreground text-sm">
          {t("bays.hold_summary", { hours: holdHours })}{" "}
          <Link
            className="text-foreground underline"
            href={locationSettingsLocationPath(locationId)}
          >
            {t("bays.configure_hold")}
          </Link>
        </p>
        {bays.length === 0 ? (
          <p>{t("bays.empty")}</p>
        ) : (
          bays.map((bay) => (
            <Card key={bay.id}>
              <CardContent className="flex flex-wrap items-center justify-between gap-4 py-4">
                <div className="min-w-0 space-y-2">
                  <p className="font-medium break-words">{bay.name}</p>
                  <Badge variant={bay.enabled ? "secondary" : "outline"}>
                    {t(bay.enabled ? "bays.enabled" : "bays.disabled")}
                  </Badge>
                </div>
                <Button
                  variant="outline"
                  aria-label={t("bays.edit_named", { name: bay.name })}
                  render={
                    <Link href={editLocationBayPath(locationId, bay.id)} />
                  }
                >
                  {t("bays.edit")}
                </Button>
              </CardContent>
            </Card>
          ))
        )}
      </PageCanvas>
    </SettingsLayout>
  );
}
